import { Injectable, inject } from '@angular/core';
import {
  createMessageTemplate$,
  deleteMessageTemplate$,
  getMessageTemplate$,
  GlobalParticipantType,
  listMessageTemplate$,
  updateMessageTemplate$,
} from '@vendasta/protos/conversation/v1';
import type {
  GetMessageTemplateRequestHydrationOptions,
  GetMessageTemplateResponse,
  ListMessageTemplateResponse,
  MessageTemplate,
  SubjectParticipant,
} from '@vendasta/protos/conversation/v1';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Observable, combineLatest, firstValueFrom, of, switchMap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ACCOUNT_GROUP_ID_TOKEN, PARTNER_ID_TOKEN } from './tokens';
@Injectable({ providedIn: 'root' })
export class TemplateService {
  private readonly snackbarService = inject(SnackbarService);

  private readonly accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN);
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);

  listTemplates(cursor: string, pageSize: number): Observable<ListMessageTemplateResponse> {
    return combineLatest([this.accountGroupId$, this.partnerId$]).pipe(
      switchMap(([accountGroupId, partnerId]) => {
        return listMessageTemplate$({
          pagingOptions: { cursor: cursor, pageSize: pageSize },
          subjectParticipant: this.buildSubjectParticipant(accountGroupId, partnerId),
        });
      }),
    );
  }

  async createTemplate(template: MessageTemplate): Promise<void> {
    const [accountGroupId, partnerId] = await firstValueFrom(combineLatest([this.accountGroupId$, this.partnerId$]));
    await firstValueFrom(
      createMessageTemplate$({
        template: template,
        subjectParticipant: this.buildSubjectParticipant(accountGroupId, partnerId),
      }),
    );
  }

  async delete(templateId: string): Promise<void> {
    return await firstValueFrom(deleteMessageTemplate$({ templateId: templateId }));
  }

  async updateTemplate(template: MessageTemplate): Promise<void> {
    await firstValueFrom(
      updateMessageTemplate$({
        template: template,
        fieldMask: {
          paths: ['name', 'content'],
        },
      }),
    );
  }

  getTemplate(
    templateId: string,
    hydrationOptions?: GetMessageTemplateRequestHydrationOptions,
  ): Observable<GetMessageTemplateResponse> {
    return getMessageTemplate$({ templateId: templateId, hydrationOptions: hydrationOptions }).pipe(
      catchError(() => {
        this.snackbarService.openErrorSnack('INBOX.SETTINGS.MESSAGE_TEMPLATES.LOAD_TEMPLATE_FAILED');
        return of(null);
      }),
    );
  }

  private buildSubjectParticipant(accountGroupId: string, partnerId: string): SubjectParticipant {
    return {
      participantType: accountGroupId
        ? GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP
        : GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_PARTNER,
      internalParticipantId: accountGroupId || partnerId,
    };
  }
}
